export type FormulaErrorKey =
    | "invalidChars"
    | "unknownQuestion"
    | "unmatchedClosing"
    | "missingOperatorBeforeOpen"
    | "doubleOperator"
    | "missingOperatorBetween"
    | "unclosedParen"
    | "endsWithOperator"
    | "emptyParen";

export const validateQuestionFormula = (
    value: string,
    allowedQuestionCodes: string[]
): FormulaErrorKey | null => {
    const cleaned = value.replace(/\s+/g, "");
    if (cleaned.length === 0) {
        return null;
    }

    const allowedSet = new Set(
        allowedQuestionCodes
            .filter((code) => typeof code === "string" && code.trim().length > 0)
            .map((code) => code.trim())
    );

    const tokenRegex = /\[(?:Preg|PREG)-[^\]]+\]|\d+(?:\.\d+)?|[+\-*/()]/g;
    const tokens = cleaned.match(tokenRegex) ?? [];
    const reconstructed = tokens.join("");
    if (reconstructed !== cleaned) {
        return "invalidChars";
    }

    let balance = 0;
    let prev: "start" | "operand" | "operator" | "left-parenthesis" | "right-parenthesis" = "start";

    for (const token of tokens) {
        if (token === "+" || token === "-" || token === "*" || token === "/") {
            if (prev === "operator" || prev === "start" || prev === "left-parenthesis") {
                return "doubleOperator";
            }
            prev = "operator";
            continue;
        }

        if (token === "(") {
            if (prev !== "operator" && prev !== "start" && prev !== "left-parenthesis") {
                return "missingOperatorBeforeOpen";
            }
            balance += 1;
            prev = "left-parenthesis";
            continue;
        }

        if (token === ")") {
            if (balance === 0) {
                return "unmatchedClosing";
            }
            if (prev === "operator" || prev === "left-parenthesis" || prev === "start") {
                return "emptyParen";
            }
            balance -= 1;
            prev = "right-parenthesis";
            continue;
        }

        if (token.startsWith("[")) {
            const match = token.match(/^\[(?:Preg|PREG)-(.+)\]$/);
            const code = match?.[1]?.trim();
            if (!code || !allowedSet.has(code)) {
                return "unknownQuestion";
            }
        }

        if (prev === "operand" || prev === "right-parenthesis") {
            return "missingOperatorBetween";
        }
        prev = "operand";
    }

    if (balance > 0) {
        return "unclosedParen";
    }
    if (prev === "operator" || prev === "left-parenthesis") {
        return "endsWithOperator";
    }

    return null;
};
